import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  Button,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Typography,
  IconButton,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import {
  useGetPlaylistsQuery,
  useAddItemToPlaylistMutation,
  useCreatePlaylistMutation,
} from 'app/api/assetApi';
import { NewPlaylistDialog } from './AddNewPlaylistDialog';
import { useFilePlayerContext } from '../FilePlayerContext';

export const AddToPlaylistDialog = ({ open, onClose, purchasedItemIds }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const { data: playlists = [] } = useGetPlaylistsQuery(undefined, { skip: !open });
  const [addItemToPlaylist] = useAddItemToPlaylistMutation();
  const [createPlaylistMutation, createPlaylistResult] = useCreatePlaylistMutation();

  const [playlistId, setPlaylistId] = useState('');
  const [showNewDialog, setShowNewDialog] = useState(false);
  const [newPlaylistTitle, setNewPlaylistTitle] = useState('');
  const { fileSelection } = useFilePlayerContext();
  const { clearAll } = fileSelection;

  const handleAdd = () => {
    if (playlistId) {
      addItemToPlaylist({ playlistId, purchasedItemIds });
      clearAll();
      onClose();
    }
  };

  const handleCreateNewPlaylist = (title) => {
    setNewPlaylistTitle(title);
    setShowNewDialog(false);
  };

  useEffect(() => {
    if (!newPlaylistTitle) return;
    createPlaylistMutation({ title: newPlaylistTitle, purchasedItemIds });
  }, [newPlaylistTitle]);

  useEffect(() => {
    if (createPlaylistResult.isSuccess && createPlaylistResult.data?.id) {
      setPlaylistId(String(createPlaylistResult.data.id));
      setNewPlaylistTitle('');
      clearAll();
      onClose();
    }
  }, [createPlaylistResult]);

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth fullScreen={isMobile}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Add to Playlist
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="subtitle1" sx={{ mt: 1 }}>
            Select a playlist
          </Typography>
          <FormControl component="fieldset" fullWidth sx={{ mt: 1 }}>
            <RadioGroup
              value={playlistId}
              onChange={(e) => setPlaylistId(e.target.value)}
            >
              {playlists.map((pl) => (
                <FormControlLabel
                  key={pl.id}
                  value={String(pl.id)}
                  control={<Radio />}
                  label={pl.title}
                />
              ))}
            </RadioGroup>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 2 }}>
          <Button variant="outlined" onClick={() => setShowNewDialog(true)}>
            New Playlist
          </Button>
          <Button onClick={handleAdd} variant="contained" disabled={!playlistId}>
            Add
          </Button>
        </DialogActions>
      </Dialog>

      <NewPlaylistDialog
        open={showNewDialog}
        playlists={playlists}
        onClose={() => setShowNewDialog(false)}
        onCreate={handleCreateNewPlaylist}
      />
    </>
  );
};

AddToPlaylistDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  purchasedItemIds: PropTypes.arrayOf(PropTypes.number).isRequired,
};
